// Where the lanes plane's rows and its sticky strip stack sit.
//
// Pure vertical geometry, extracted from the view because several surfaces have
// to agree on it and one of them getting it wrong is invisible until something
// lands in the wrong place. The strip stack's offsets in particular exist ONCE
// on purpose: the geometry bug this plane has actually shipped was two copies of
// that sum drifting apart when a strip was added to one and not the other.
import { nearestMarks } from "./traceLanes.js";

// The rail's DEFAULT width, and the heights of every band stacked above and
// inside the plane. The heights are fixed; the only thing that scales with
// zoom is the per-turn width. The rail's width is the reader's to drag, within
// bounds that keep a title readable on one side and the axis on the other;
// every consumer reads the scene's `railW`, this constant only seeds it.
export const RAIL_W = 240;
export const RAIL_W_MIN = 160;
export const RAIL_W_MAX = 520;
export function clampRailW(w) {
  if (w == null || w === "") return RAIL_W;
  const n = Number(w);
  if (!Number.isFinite(n)) return RAIL_W;
  return Math.round(Math.max(RAIL_W_MIN, Math.min(RAIL_W_MAX, n)));
}
// A lane row carries the trace listing's two-line entry (an 11px title over
// a sparkline) as one unit, with air above the title and below the sparkline
// so each row reads as its own thing (24 and 28 read as a stack of curves at
// the 12px title, found live; 30 held the 11px one with the title's
// descenders on the sparkline's peaks, so 32 puts two pixels between them).
export const ROW_H = 32;
export const HEAD_H = 32;
// A top-level group row (the eyebrow heads) takes three more pixels above and
// below: at the lane row's height the uppercase, tracked eyebrow read as
// cramped beside the title rows it introduces, and two each way was still a
// touch short (found live). Every place that sizes a head reads headHeight,
// so the two heights cannot drift apart.
export const GROUP_H = HEAD_H + 6;
// A SESSION head (a plane holding several sessions) carries two lines, the
// session's name over where and when it ran, so it is taller than a group.
export const SESSION_H = 44;
// A COMPACT row (a session card's, where many sessions share one screen) is one
// line: the label, no sparkline, since the card's own axis already draws the
// row's activity.
export const COMPACT_H = 22;
export function headHeight(row) {
  if (row.session) return SESSION_H;
  if (row.compact) return COMPACT_H;
  return row.depth > 0 ? HEAD_H : GROUP_H;
}
export function rowHeight(row) {
  if (row.type === "head") return headHeight(row);
  return row.compact ? COMPACT_H : ROW_H;
}
// Every band in the strip stack is the same height: four near-equal heights
// (36, 30, 24, 20) read as a mistake rather than a design (found live). The
// axis band is one of them: the turn ruler, the topic ground and the token
// skyline share its 24px rather than stacking three bands over the rows.
export const STRIP_H = 24;
export const RULER_H = STRIP_H;
export const REQUESTS_H = STRIP_H;
export const REPORTS_H = STRIP_H;
export const REVIEW_H = STRIP_H;
export const HANDOFF_H = STRIP_H;
export const READING_H = STRIP_H;
export const JOINS_H = STRIP_H;
// A spliced sub-agent inset sizes to its CONTENT, reported by its own
// ResizeObserver; this is the fallback until the first measure lands.
export const INSET_FALLBACK_H = 96;
// Stacked edge chips: one chip's height plus a hairline, and a ceiling so a
// pathological set of related rows cannot paper over the timeline.
export const CHIP_STACK_H = 18;
export const CHIP_STACK_MAX = 4;
// The share of the column a dock takes until the reader drags its divider.
export const DOCK_SHARE = 0.46;
// The session canvas holds several sessions' cards over the dock, so the dock
// starts lower there; a dragged height still wins.
export const SESSION_DOCK_SHARE = 0.32;

// The one source of truth for the sticky-strip stack's geometry: every
// strip's `top` and the row-top accumulator read THIS. The geometry bug this
// file has actually shipped was exactly two copies of this sum drifting
// apart (a strip added to one and not the other), so the sum exists once.
export function stripOffsets({ requestsOpen = false, reportsOpen = false, findingsOpen = false, reviewOpen = false, handoffOpen = false, readingOpen = false, joinsOpen = false } = {}) {
  // The strips that open on their own lead the scan stack, in the order of
  // what they are about: a session axis's captured requests first (the axis's
  // own structure) -- only when the axis band has no token skyline to carry
  // them, which is where they are drawn otherwise -- then the pipeline's review, ONE band
  // holding its reports (the chapters every other mark sits inside) and its
  // findings (the exceptions within them). Both sit where the eye lands before
  // any on-demand reading is asked for.
  const requests = RULER_H;
  const reports = requests + (requestsOpen ? REQUESTS_H : 0);
  const findings = reports;
  const review = reports + (reportsOpen || findingsOpen ? REPORTS_H : 0);
  const handoff = review + (reviewOpen ? REVIEW_H : 0);
  const reading = handoff + (handoffOpen ? HANDOFF_H : 0);
  const joins = reading + (readingOpen ? READING_H : 0);
  const rows = joins + (joinsOpen ? JOINS_H : 0);
  return { requests, reports, findings, review, handoff, reading, joins, rows, readingHeight: READING_H };
}

// The y of every row's top, from the first row down. An inset row sizes to its
// own measured content rather than to a fixed height, which is what lets one
// plane hold a child timeline with a different clock.
export function rowTopsOf(rows, stripRowsTop, insetHeights = null) {
  const tops = [];
  let y = stripRowsTop;
  for (const row of rows) {
    tops.push(y);
    y += row.type === "head"
      ? headHeight(row)
      : row.type === "inset"
        ? insetHeights?.get(row.lane.id) ?? INSET_FALLBACK_H
        : ROW_H;
  }
  return tops;
}

// The y a lane's annotations are drawn at, including the redirection a FOLDED
// row performs: a lane folded into its header carries its badges on the header
// row, so anything that addresses a lane by row -- the influence overlay's
// endpoints, the join pins, the follow rings -- has to resolve through this map
// or it draws at no row at all.
export function laneCentres(rows, rowTops) {
  const map = new Map();
  rows.forEach((row, i) => {
    if (row.type === "lane") map.set(row.lane.id, rowTops[i] + ROW_H / 2);
    else if (row.laneIds) for (const id of row.laneIds) map.set(id, rowTops[i] + headHeight(row) / 2);
  });
  return map;
}

// Which lanes actually chart which turns, for the found-live rule that a pin or
// a ring needs a charted cell to sit on: a floating annotation reads as
// highlighting something that does not exist.
export function laneCellsById(rows) {
  const map = new Map();
  for (const row of rows) {
    if (row.type === "lane" && row.lane?.cells) map.set(row.lane.id, row.lane.cells);
  }
  return map;
}

// Edge pointers for lanes whose activity is outside the visible window: where
// the nearest mark is, in which direction, one click to go there. The walk
// over every lane is the expensive half, so it is computed from the window's
// TURN edges (which move only when scrolling crosses a turn boundary) and
// memoized by the caller; the per-frame work is pinning the chips' x to the
// viewport edge, which is a multiplication per chip.
export function pointerChips(rows, rowTops, fromTurn, toTurn) {
  const chips = [];
  rows.forEach((row, i) => {
    if (row.type !== "lane" || !row.lane.turns.length) return;
    const near = nearestMarks(row.lane, fromTurn, toTurn);
    if (near.visible) return;
    const top = rowTops[i] + (ROW_H - 16) / 2;
    if (near.left != null) chips.push({ key: `${row.lane.id}:l`, lane: row.lane, turn: near.left, dir: "left", top });
    if (near.right != null) chips.push({ key: `${row.lane.id}:r`, lane: row.lane, turn: near.right, dir: "right", top });
  });
  return chips;
}

// Where a related row sits relative to the band actually on screen. The twin of
// a file can be twenty rows away in another subtree, and the outline that marks
// it is no use to a reader who cannot see the row. Rows in view get a chip at
// their own height, which is the attention draw; rows out of view get one pinned
// to the edge they left, carrying the direction to travel.
export function relatedChips(rows, rowTops, relatedPaths, view, headerH = 0) {
  if (!relatedPaths.size) return [];
  const out = [];
  // The ruler and the density strip are sticky, so the top of the band a reader
  // can actually see is below them, not at the viewport edge. A row tucked under
  // them is gone as far as the reader is concerned, and a chip pinned to the
  // edge lands behind them.
  const bandTop = view.top + headerH;
  // Chips pinned to an edge all want the same coordinate, so they stack there
  // and render on top of each other. Off-band chips are laid out in a column
  // from the edge they left; in-band ones sit at their own row and cannot
  // collide, because a row is taller than a chip.
  let ups = 0;
  let downs = 0;
  rows.forEach((row, i) => {
    if (row.type !== "lane" || !row.lane.path || !relatedPaths.has(row.lane.path)) return;
    const rowTop = rowTops[i];
    // Before the viewport has been measured there is no band to be outside of,
    // so the chip still points at the row and simply claims no direction. An
    // arrow guessed from a zero height would be a claim about where the row is.
    const known = view.height > 0;
    const above = known && rowTop + ROW_H < bandTop;
    const below = known && rowTop > view.top + view.height;
    const slot = above ? ups++ : below ? downs++ : 0;
    if (slot >= CHIP_STACK_MAX) return;
    out.push({
      key: `rel:${row.lane.id}`,
      lane: row.lane,
      rowTop,
      dir: above ? "up" : below ? "down" : null,
      top: above
        ? bandTop + 2 + slot * CHIP_STACK_H
        : below
          ? view.top + view.height - 18 - slot * CHIP_STACK_H
          : rowTop + (ROW_H - 16) / 2,
    });
  });
  return out;
}


// The rows the cells a strip mark stands for sit on: each target's own lane
// row, or the folded head that stands for its lane, with that row's label, top
// and height in plane coordinates. A lane no row stands for is dropped, and
// targets sharing a row and a turn are one.
export function guideTargets(rows, rowTops, targets) {
  if (!targets?.length) return [];
  const rowOf = new Map();
  rows.forEach((row, i) => {
    if (row.type === "lane") rowOf.set(row.lane.id, i);
    else if (row.laneIds) for (const id of row.laneIds) if (!rowOf.has(id)) rowOf.set(id, i);
  });
  const out = [];
  const seen = new Set();
  for (const t of targets) {
    const i = rowOf.get(t.laneId);
    if (i == null) continue;
    const key = `${i}:${t.turn}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const row = rows[i];
    out.push({
      laneId: t.laneId,
      turn: t.turn,
      label: row.type === "lane" ? row.lane.label : row.label,
      folded: row.type !== "lane",
      rowTop: rowTops[i],
      rowH: row.type === "lane" ? ROW_H : headHeight(row),
    });
  }
  return out;
}

// Where a hovered strip mark points when none of the cells it stands for is in
// the band on screen: at the nearest, from the edge it left, with how many more
// lie that way. Null while one is in view -- the mark's own column already
// leads the eye there -- and before the viewport is measured, when no direction
// is known. `bottomInset` is what a band pinned to the bottom edge covers.
export function guideChip(targets, view, headerH = 0, bottomInset = 0) {
  if (!targets?.length || !(view?.height > 0)) return null;
  const bandTop = view.top + headerH;
  const bandBottom = view.top + view.height - bottomInset;
  let best = null;
  let ups = 0;
  let downs = 0;
  for (const t of targets) {
    const bottom = t.rowTop + (t.rowH ?? ROW_H);
    const above = bottom <= bandTop;
    const below = t.rowTop >= bandBottom;
    if (!above && !below) return null;
    if (above) ups += 1;
    else downs += 1;
    const dist = above ? bandTop - bottom : t.rowTop - bandBottom;
    if (!best || dist < best.dist) best = { target: t, dir: above ? "up" : "down", dist };
  }
  return {
    target: best.target,
    dir: best.dir,
    more: (best.dir === "up" ? ups : downs) - 1,
    top: best.dir === "up" ? bandTop + 2 : bandBottom - 18,
  };
}

// The scroll that brings a guide's row to the middle of the band under the
// sticky headers.
export function guideScrollTop(target, viewHeight, headerH = 0) {
  return Math.max(0, target.rowTop + (target.rowH ?? ROW_H) / 2 - (viewHeight + headerH) / 2);
}

// Where a click on a strip mark scrolls: the nearest of its rows to the middle
// of the band, or nowhere when one is already in it. The band is the one the
// hover pointer judged against: under the sticky headers, and above a band
// pinned to the bottom edge (`bottomInset`).
export function guideReveal(targets, { scrollTop = 0, viewHeight = 0, headerH = 0, bottomInset = 0 } = {}) {
  const g = guideChip(targets, { top: scrollTop, height: viewHeight }, headerH, bottomInset);
  return g ? guideScrollTop(g.target, viewHeight - bottomInset, headerH) : null;
}

